/**
 * A escolha de colunas da planilha (2026-11-01) — pura e testada, pra o
 * pop-up só desenhar.
 *
 * **Padrão intacto = `columns` AUSENTE no pedido.** O colunas padrão é
 * o das colunas `default` do catálogo; quando a seleção é exatamente
 * essa, a tela não manda lista nenhuma e o servidor usa o layout de
 * sempre. Mandar a lista nesse caso congelaria no pedido um layout que
 * deveria acompanhar o catálogo.
 *
 * **A ordem na planilha é a do catálogo**, nunca a do clique: a seleção
 * é um conjunto, e `orderedSelection` a devolve na ordem do servidor.
 *
 * **A última escolha mora no navegador** (`localStorage`), como as
 * outras preferências de vista; chave que o catálogo não conhece mais
 * (coluna que saiu) é descartada ao ler.
 */

import type { ExportColumn } from "@/shared/api/api-client";
import { EXPORT_THEME_LAYOUTS, type ExportThemeLayout } from "@/shared/vocabulary/exports";

export const EXPORT_COLUMNS_STORAGE_KEY = "reputaition.export_columns";

export type ColumnChoice = {
  columns: string[];
  header: boolean;
  theme_layout: ExportThemeLayout;
  highlight_only: boolean;
};

/** Valor desconhecido (storage velho ou adulterado) cai no padrão. */
export function sanitizeThemeLayout(value: unknown): ExportThemeLayout {
  return (EXPORT_THEME_LAYOUTS as readonly unknown[]).includes(value)
    ? (value as ExportThemeLayout)
    : "columns";
}

/** As chaves do colunas padrão, na ordem do catálogo. */
export function defaultKeys(catalog: readonly ExportColumn[]): string[] {
  return catalog.filter(column => column.group === "default").map(column => column.key);
}

/** A seleção na ORDEM DO CATÁLOGO (o que não existe nele sai). */
export function orderedSelection(
  catalog: readonly ExportColumn[],
  selected: ReadonlySet<string>
): string[] {
  return catalog.filter(column => selected.has(column.key)).map(column => column.key);
}

/** `undefined` quando a seleção é o colunas padrão (o pedido não manda
 *  `columns`); senão a lista ordenada. */
export function columnsForRequest(
  catalog: readonly ExportColumn[],
  selected: ReadonlySet<string>
): string[] | undefined {
  const ordered = orderedSelection(catalog, selected);
  const standard = defaultKeys(catalog);
  const isStandard =
    ordered.length === standard.length && ordered.every((key, index) => key === standard[index]);
  return isStandard ? undefined : ordered;
}

export const RAW_PREFIX = "raw:";

/** Chave de coluna da planilha da clipadora. O servidor aceita qualquer
 *  `raw:<nome>`, então ela vale mesmo que o catálogo de hoje não a tenha. */
export function isRawKey(key: string): boolean {
  return key.startsWith(RAW_PREFIX) && key.length > RAW_PREFIX.length;
}

/** O catálogo mais uma coluna `raw` (com o nome cru) pra cada chave
 *  `raw:` de `keys` que ele não tem — a planilha salva pode citar coluna
 *  que a empresa ainda não subiu, e ela tem que aparecer marcada. */
export function withOrphanRaw(
  catalog: readonly ExportColumn[],
  keys: readonly string[]
): ExportColumn[] {
  const known = new Set(catalog.map(column => column.key));
  const extra: ExportColumn[] = [];
  for (const key of keys) {
    if (!isRawKey(key) || known.has(key)) continue;
    known.add(key);
    extra.push({ key, label: key.slice(RAW_PREFIX.length), group: "raw", note: null });
  }
  return extra.length > 0 ? [...catalog, ...extra] : [...catalog];
}

/** Só chaves que o catálogo conhece (e `raw:` de qualquer nome), sem repetição. */
export function sanitizeColumns(
  catalog: readonly ExportColumn[],
  keys: readonly unknown[]
): string[] {
  const known = new Set(catalog.map(column => column.key));
  const seen = new Set<string>();
  const out: string[] = [];
  for (const key of keys) {
    if (typeof key !== "string" || !(known.has(key) || isRawKey(key)) || seen.has(key)) continue;
    seen.add(key);
    out.push(key);
  }
  return out;
}

export function readColumnChoice(): ColumnChoice | null {
  try {
    const raw = window.localStorage.getItem(EXPORT_COLUMNS_STORAGE_KEY);
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return null;
    const { columns, header, theme_layout, highlight_only } = parsed as {
      columns?: unknown;
      header?: unknown;
      theme_layout?: unknown;
      highlight_only?: unknown;
    };
    if (!Array.isArray(columns)) return null;
    return {
      columns: columns.filter((key): key is string => typeof key === "string"),
      header: header !== false,
      theme_layout: sanitizeThemeLayout(theme_layout),
      highlight_only: highlight_only === true
    };
  } catch {
    return null;
  }
}

export function writeColumnChoice(choice: ColumnChoice): void {
  try {
    window.localStorage.setItem(EXPORT_COLUMNS_STORAGE_KEY, JSON.stringify(choice));
  } catch {
    // Storage indisponível: a escolha só não fica lembrada.
  }
}

/** A seleção inicial do pop-up: a lembrada (saneada contra o catálogo)
 *  ou, sem ela ou sem sobrar nada, o colunas padrão. */
export function initialSelection(
  catalog: readonly ExportColumn[],
  remembered: ColumnChoice | null
): {
  selected: Set<string>;
  header: boolean;
  theme_layout: ExportThemeLayout;
  highlight_only: boolean;
} {
  if (remembered) {
    const kept = sanitizeColumns(catalog, remembered.columns);
    const { header, theme_layout, highlight_only } = remembered;
    return {
      selected: new Set(kept.length > 0 ? kept : defaultKeys(catalog)),
      header,
      theme_layout,
      highlight_only
    };
  }
  return {
    selected: new Set(defaultKeys(catalog)),
    header: true,
    theme_layout: "columns",
    highlight_only: false
  };
}
